"use client";

import { useState, useEffect } from "react";
import { Search, Loader2, Package, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ShopProductCard } from "./ShopProductCard";
import { ProductPagination, ShopProduct } from "@/lib/types/shop";
import { cn } from "@/lib/utils";

// Shimmer skeleton for loading more products
function ProductCardSkeleton() {
  return (
    <div className="space-y-3">
      <div className="aspect-[3/4] bg-gray-200 rounded-lg animate-pulse" />
      <div className="space-y-2">
        <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse" />
        <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse" />
      </div>
    </div>
  );
}

interface ProductGridProps {
  products: ShopProduct[];
  pagination: ProductPagination;
  onLoadMore: () => void;
  onClearFilters?: () => void;
  isLoadingMore?: boolean;
  className?: string;
  hasAnyProducts?: boolean;
  activeFiltersCount?: number;
  gridCols?: number;
  productUrlPrefix?: string; // e.g., "/fashion" for marketplace
  industry?: "fashion" | "electronics"; // Industry for card styling
}

export function ProductGrid({
  products,
  pagination,
  onLoadMore,
  onClearFilters,
  isLoadingMore = false,
  className,
  hasAnyProducts = true,
  activeFiltersCount = 0,
  gridCols = 4,
  productUrlPrefix = "",
  industry,
}: ProductGridProps) {
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Track scroll position for back-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const gridColsClass = gridCols === 4 
    ? "grid-cols-2 lg:grid-cols-4" 
    : "grid-cols-2 lg:grid-cols-3";

  // Initial loading skeleton (when no products yet)
  if (products.length === 0 && isLoadingMore) {
    return (
      <div className={cn("space-y-8", className)}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className={cn("grid gap-4 sm:gap-6 lg:gap-8", gridColsClass)}>
            {[...Array(gridCols === 4 ? 8 : 6)].map((_, i) => (
              <ProductCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Empty state - differentiate between no products at all vs filters returning no results
  if (products.length === 0) {
    if (activeFiltersCount > 0 || !hasAnyProducts === false) {
      return (
        <div className={cn("flex items-center justify-center min-h-[400px]", className)}>
          <div className="text-center max-w-md">
            <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
              <Search className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No products match your filters
            </h3>
            <p className="text-gray-500 mb-6">
              Try adjusting your search or filters to find what you're looking for.
            </p>
            {onClearFilters && (
              <Button
                onClick={onClearFilters}
                variant="outline"
                className="border-black text-black hover:bg-black hover:text-white transition-colors"
              >
                Clear All Filters
              </Button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className={cn("flex items-center justify-center min-h-[400px]", className)}>
        <div className="text-center max-w-md">
          <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
            <Package className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No products yet
          </h3>
          <p className="text-gray-500">
            This store hasn't added any products yet. Check back soon!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-8", className)}>
      {/* Header with Results Count */}
      <div className="flex items-center gap-4 mb-6">
        <p className="text-sm text-gray-500">
          <span className="hidden sm:inline">
            Showing {products.length} of {pagination.total} products
          </span>
          <span className="sm:hidden">{pagination.total} products</span>
        </p>
      </div>

      {/* Products Grid */}
      <div className={cn("grid gap-4 sm:gap-6 lg:gap-8", gridColsClass)}>
        {products.map((product) => (
          <ShopProductCard key={product._id} product={product} productUrlPrefix={productUrlPrefix} industry={industry} />
        ))}
        
        {/* Shimmer skeletons while loading more */}
        {isLoadingMore && (
          <>
            {[...Array(gridCols === 4 ? 4 : 3)].map((_, i) => (
              <ProductCardSkeleton key={`skeleton-${i}`} />
            ))}
          </>
        )}
      </div>

      {/* Load More Button */}
      {pagination.hasNext && !isLoadingMore && (
        <div className="flex flex-col items-center gap-3 pt-8">
          <p className="text-sm text-gray-500">
            Showing {products.length} of {pagination.total} products
          </p>
          <Button
            onClick={onLoadMore}
            className="bg-black text-white hover:bg-gray-800 h-12 px-8 font-medium flex items-center gap-2"
          >
            <ChevronDown className="h-4 w-4" />
            See More Products
          </Button>
        </div>
      )}

      {/* End of results message */}
      {!pagination.hasNext && products.length > 0 && pagination.total > pagination.limit && (
        <div className="text-center pt-8">
          <p className="text-sm text-gray-500">
            You've seen all {pagination.total} products
          </p>
        </div>
      )}

      {/* Floating Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 bg-black text-white p-3 rounded-full shadow-lg hover:bg-gray-800 transition-all duration-300 hover:scale-110"
          aria-label="Back to top"
        >
          <ChevronUp className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}
